import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { openDialog } from "redux/features/dialog/dialogSlice"
import { selectSettingsById } from "redux/features/settings/settingsSlice"
import { formatDate, formatMoney } from "helpers/formatters"
import {
  isPaymentRequestsCreate,
  isPaymentRequestsPayAll,
} from "helpers/features"
import { PAYMENT_PAY_MODAL, SEPA_PAYMENT_PAY_MODAL } from "config"
import Doughnut from "components/ui/Doughnut"
import Title from "components/Debt/Title"
import Manager from "components/Debt/Manager"
import Summary from "components/Debt/Summary"
import Information from "components/ui/InfoList"
import Card from "components/ui/CardAccordion"
import {
  CollapsedAside,
  Balance,
  CollapsedActions,
  CollapsedPrimaryButton,
  Container,
  Aside,
  Content,
  Info,
  ActionList,
  ActionListItem,
  PrimaryButton,
} from "components/Debt/elements"
import { DebtType, SettingsType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  item: DebtType
}

const DebtItemPaid = ({ item }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const settings = useAppSelector(
    (state) => selectSettingsById(state, 0) as SettingsType,
  )

  const { id, start_date, balance, unpaid_total_sum, customer, manager } = item

  const isUnpaid = Boolean(unpaid_total_sum && unpaid_total_sum > 0)

  const general = [
    {
      label: t("features.debt.list.start_date.title"),
      value: formatDate(start_date),
      visible: true,
    },
    {
      label: t("features.debt.list.customer.title"),
      value: customer?.name,
      visible: true,
    },
  ].filter((item) => item.value && item.visible)

  const handlePaymentCreate = (
    event: React.SyntheticEvent,
    amount: number | undefined,
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    dispatch(
      openDialog({
        type: isPaymentRequestsCreate(settings?.features)
          ? PAYMENT_PAY_MODAL
          : SEPA_PAYMENT_PAY_MODAL,
        content: {
          debtId: id,
          amount: amount,
        },
      }),
    )
  }

  return (
    <Card
      collapsed={
        <>
          <CollapsedAside>
            <Title compact debt={item} />
            <Balance variant="h4">{formatMoney(balance)}</Balance>
          </CollapsedAside>
          {isPaymentRequestsPayAll(settings?.features) && isUnpaid && (
            <CollapsedActions>
              <CollapsedPrimaryButton
                onClick={(event) =>
                  handlePaymentCreate(event, unpaid_total_sum)
                }
              >
                {t("features.debt.actions.payment.create.title", {
                  amount: formatMoney(unpaid_total_sum),
                })}
              </CollapsedPrimaryButton>
            </CollapsedActions>
          )}
        </>
      }
      expanded={
        <Container>
          <Aside>
            <Doughnut
              color="primary"
              top={t("features.debt.doughnut.balance.title")}
              amount={formatMoney(balance)}
              value={100}
            />
            <Summary item={item} />
          </Aside>
          <Content>
            <Info>
              <Title debt={item} />
              <Manager dense manager={manager} />
              <Information dense items={general} />
            </Info>
            {isPaymentRequestsPayAll(settings?.features) && isUnpaid && (
              <ActionList>
                <ActionListItem>
                  <PrimaryButton
                    onClick={(event) =>
                      handlePaymentCreate(event, unpaid_total_sum)
                    }
                  >
                    {t("features.debt.actions.payment.fully.title")}
                  </PrimaryButton>
                </ActionListItem>
              </ActionList>
            )}
          </Content>
        </Container>
      }
    />
  )
}

export default DebtItemPaid
